<template>
  <div style="clear:both;">
    <v-dialog v-model="addManualTaskDialog" persistent max-width="1100">
      <v-card max-width="1200">
        <v-toolbar dense>
          <v-toolbar-title>{{ $t('Add_Manual_Task') }}</v-toolbar-title>
        </v-toolbar>
        <v-form @submit.prevent="true" autocomplete="off">
          <v-container fluid>
            <v-row>
              <v-col cols="6">
                <v-text-field
                  required
                  :rules="NameRules"
                  :label="$t('Task')"
                  variant="underlined"
                  v-model="task.name"
                  auto-grow
                  :error-messages="taskNameMessages"
                ></v-text-field>
                <v-select
                  :label="$t('Required_In_Order_To')"
                  item-title="value"
                  item-value="key"
                  v-model="task.taskListType"
                  :items="availableTaskListTypes"
                  required
                  variant="underlined"
                  :error-messages="taskTypeErrors"
                  return-object
                ></v-select>
                <v-text-field
                  :error-messages="stepErrors"
                  type="number"
                  placeholder="Type number"
                  :label="$t('Step')"
                  variant="underlined"
                  v-model="task.step"
                  min="0"
                ></v-text-field>
                <people-picker
                  :user="task.assignedTo"
                  v-model="task.assignedTo"
                  label="Assigned to"
                  :includeDistroService="true"
                ></people-picker>

                <v-row>
                  <v-col cols="12">
                    <label>{{ $t('Supporting_Links') }} ({{ task.supportingLinks ? task.supportingLinks.length : 0 }})</label>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-btn icon @click="addSupportingLink" flat v-bind="props">
                          <v-icon>mdi-plus-circle-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('Add_Supporting_Link') }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>
                <v-row style="padding-bottom: 8px" v-bind:key="item.clientId" v-for="item in task.supportingLinks">
                  <v-col cols="5" class="py-0">
                    <v-text-field v-model="item.url" :label="$t('URL')" variant="underlined"></v-text-field>
                  </v-col>
                  <v-col cols="5" class="py-0">
                    <v-text-field v-model="item.name" :label="$t('Name')" variant="underlined"></v-text-field>
                  </v-col>
                  <v-col cols="2" class="py-0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-btn @click="deleteSupportLinks(item)" icon flat v-bind="props">
                          <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('Delete') }}</span>
                    </v-tooltip>                    
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <label>{{ $t('Documented_Information_Link') }} ({{ task.standards ? task.standards.length : 0 }})</label>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-btn icon @click="addStandard" flat v-bind="props">
                          <v-icon>mdi-plus-circle-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('Add_Documented_Information') }}</span>
                    </v-tooltip>
                  </v-col>
                </v-row>

                <v-row style="padding-bottom: 8px" v-bind:key="item.clientId" v-for="item in task.standards"
                  ><!-- had column -->
                  <v-col cols="5" class="py-0">
                    <v-text-field v-model="item.url" label="URL" variant="underlined"></v-text-field>
                  </v-col>
                  <v-col cols="5" class="py-0">
                    <v-text-field v-model="item.name" label="Name" variant="underlined"></v-text-field>
                  </v-col>
                  <v-col cols="2" class="py-0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-btn @click="deleteStandard(item)" icon v-bind="props" flat>
                          <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('Delete') }}</span></v-tooltip
                    >
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="6">
                <label>{{ $t('Possible_Responses') }}</label>
                <v-tooltip bottom>
                  <template v-slot:activator="{ props }">
                    <v-btn icon @click="addStatus" flat v-bind="props">
                      <v-icon>mdi-plus-circle-outline</v-icon>
                      </v-btn>
                  </template>
                  <span>{{ $t('Add_Possible_Responses') }}</span>
                </v-tooltip>

                <p class="error--text v-messages mt-1" v-if="typeof itemStatusMessage === 'string'">{{ itemStatusMessage }}</p>
                <v-row v-if="task.itemStatus && task.itemStatus.length > 0">
                  <v-col cols="5">{{ $t('Response') }}</v-col>
                  <v-col cols="2">{{ $t('Task_Satisfied') }}</v-col>
                  <v-col cols="2">{{ $t('Display_Order') }}</v-col>
                </v-row>
                <v-row justify="center" v-bind:key="item.clientId" v-for="(item, index) in task.itemStatus">
                  <v-col cols="5" class="py-0">
                    <v-text-field required 
                      variant="underlined"
                      v-model="item.name" :error-messages="itemStatusNameMessages(index)"></v-text-field>
                  </v-col>
                  <v-col cols="2" class="py-0">
                    <v-checkbox v-model="item.required" :error-messages="itemStatusRequired()"></v-checkbox>
                  </v-col>
                  <v-col cols="2" class="py-0">
                    <v-text-field
                      required
                      variant="underlined"
                      :error-messages="itemStatusDisplayOrderMessages(index)"
                      v-model="item.displayOrder"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3" class="py-0">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ props }">
                        <v-btn @click="deleteItemStatus(item)" icon v-bind="props" flat>
                          <v-icon>mdi-delete-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>{{ $t('Delete') }}</span></v-tooltip
                    >
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
            <v-card-actions>
              <v-row justify="end">
                <v-btn :disabled="!valid()" class="success" @click="save">Save</v-btn>
                <v-btn
                  text
                  @click="
                    closeParent();
                    addManualTaskDialog = false
                  "
                  >{{ $t('Cancel') }}</v-btn
                >
              </v-row>
            </v-card-actions>
          </v-container>
        </v-form>
      </v-card>
    </v-dialog>

    <toaster ref="toaster"></toaster>
  </div>
</template>

<script>
import PeoplePicker from '@/components/peoplePicker/PeoplePicker'
import { TaskListTypes } from '@/common/types'
import TaskService from '@/services/task.service'
import { AddedVia } from '@/common/task'
import { FormStatus } from '@/common'
import Toaster from '@/components/Toaster'
import MOCService from '@/services/moc.service'
export default {
  data() {
    return {
      addManualTaskDialog: true,
      minimumNameLength: 1,
      maximumNameLength: 500,
      task: {}
    }
  },
  components: {
    PeoplePicker,
    Toaster
  },
  props: {
    mocId: {
      type: String
    },
    mocStatus: {
      type: Object
    }
  },
  computed: {
    availableTaskListTypes() {
      switch (this.mocStatus.value) {
        case FormStatus.properties[FormStatus.NEW].value:
        case FormStatus.properties[FormStatus.DRAFT].value:
        case FormStatus.properties[FormStatus.SENTBACKFORCORRECTIONS].value:
        case FormStatus.properties[FormStatus.BEINGREVISED].value:
          return Object.values(TaskListTypes.properties).filter(x => x.key !== 'all')
        case FormStatus.properties[FormStatus.SUBMITTED].value:
          return Object.values(TaskListTypes.properties).filter(x => x.key !== 'all' && x.key !== 'submit')
        case FormStatus.properties[FormStatus.AVAILABLEFORDECISION].value:
          return Object.values(TaskListTypes.properties).filter(
            x => x.key !== 'all' && x.key !== 'submit' && x.key !== 'sendfordecision'
          )
        case FormStatus.properties[FormStatus.PENDINGOWNERDECISION].value:
        case FormStatus.properties[FormStatus.OWNERAPPROVEDDEFERREDSTARTDATE].value:
          return Object.values(TaskListTypes.properties).filter(
            x => x.key !== 'all' && x.key !== 'submit' && x.key !== 'sendfordecision' && x.key !== 'enableownerdecisiontooccur'
          )
        case FormStatus.properties[FormStatus.AVAILABLETOIMPLEMENT].value:
          return Object.values(TaskListTypes.properties).filter(
            x =>
              x.key !== 'all' &&
              x.key !== 'submit' &&
              x.key !== 'enableownerdecisiontooccur' &&
              x.key !== 'proceedwithimplementation' &&
              x.key !== 'sendfordecision'
          )
        case FormStatus.properties[FormStatus.CHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
        case FormStatus.properties[FormStatus.CHANGEISFULLYIMPLEMENTED].value:
        case FormStatus.properties[FormStatus.TEMPORARYCHANGEISINPLACEWITHCONSEQUENTIALACTIONSREQUIRED].value:
          return Object.values(TaskListTypes.properties).filter(
            x =>
              x.key !== 'all' &&
              x.key !== 'submit' &&
              x.key !== 'enableownerdecisiontooccur' &&
              x.key !== 'proceedwithimplementation' &&
              x.key !== 'sendfordecision' &&
              x.key !== 'confirmchangeisinplace'
          )
      }
      return null
    },
    itemStatusMessage: {
      get: function() {
        return !this.task.itemStatus || this.task.itemStatus.length <= 0 ? this.$t('At_Least_One_Item_Status_Is_Required') : false
      }
    },
    NameRules: {
      get: function() {
        return [
          v => !!v || 'Task is required',
          v =>
            (v && v.trim().length >= this.minimumNameLength) || this.$t('Rule_MinimumLength', { length: this.minimumNameLength }),
          v =>
            (v && v.trim().length <= this.maximumNameLength) || this.$t('Rule_MaximumLength', { length: this.maximumNameLength })
        ]
      }
    },
    stepErrors: {
      get: function() {
        if (this.task.step === '') return this.$t('Step_Is_Required')

        if (this.task.step < 0) return this.$t('This_Field_Cannot_be_Less_Than_Zero')

        return null
      }
    },

    taskNameMessages: {
      get: function() {
        return !this.task.name ? this.$t('Task_Is_Required') : null
      }
    },
    taskTypeErrors: {
      get: function() {
        return this.task.taskListType == null ? this.$t('Required_In_Order_To_Is_Required') : null
      }
    }
  },
  methods: {
    addSupportingLink() {
      if (!this.task.supportingLinks) {
        this.task.supportingLinks = []
      }
      let newLink = {
        url: '',
        name: '',
        clientId: new Date().getTime()
      }
      this.task.supportingLinks.push(newLink)
    },
    addStandard() {
      if (!this.task.standards) {
        this.task.standards = []
      }
      let newLink = {
        url: '',
        name: '',
        clientId: new Date().getTime()
      }
      this.task.standards.push(newLink)
    },
    addStatus() {
      if (!this.task.itemStatus) {
        this.task.itemStatus = []
      }
      let newStatus = {
        name: '',
        required: false,
        clientId: new Date().getTime(),
        displayOrder: ''
      }
      this.task.itemStatus.push(newStatus)
    },
    createNewTask() {
      TaskService.getNewTask(this.mocId, this.$t('Completed'))
        .then(data => {
          this.task = data

          this.task.active = true
          this.task.addedVia = AddedVia.properties[AddedVia.MANUAL].key
        })
        .catch(e => {
          // TODO: deterimine if we need to do anything here
          this.$refs.toaster.pop('created error', e)
        })
        .finally()
    },
    closeParent() {
      this.$emit('close')
    },
    deleteItemStatus(item) {
      this.task.itemStatus = this.task.itemStatus.filter(i => (i.id ? i.id : i.clientId) !== (item.id ? item.id : item.clientId))
    },
    deleteStandard(item) {
      this.task.standards = this.task.standards.filter(i => (i.id ? i.id : i.clientId) !== (item.id ? item.id : item.clientId))
    },

    deleteSupportLinks(item) {
      this.task.supportingLinks = this.task.supportingLinks.filter(
        i => (i.id ? i.id : i.clientId) !== (item.id ? item.id : item.clientId)
      )
    },
    itemStatusDisplayOrderMessages(index) {
      return !this.task.itemStatus[index].displayOrder && this.task.itemStatus[index].displayOrder !== 0 ? 'Required' : null
    },
    itemStatusNameMessages(index) {
      return !this.task.itemStatus[index].name ? 'Required' : null
    },
    itemStatusRequired: function() {
      return this.task.itemStatus.filter(i => {
        return i.required
      }).length <= 0
        ? this.$t('One_Item_Must_Complete_The_Task')
        : null
    },
    save: function() {
      this.task.mocId = this.mocId

      if ((this.task.supportingLinks || []).length > 0) {
        this.task.supportingLinks.forEach(t => {
          t.url = t.url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '')
        })
      }

      TaskService.saveSingleTask(this.task)
        .then(data => {
          if (this.task.assignedTo && this.task.assignedTo.id && !this.task.completed) {
            MOCService.notifyTaskAssignment(this.mocId, this.task).catch(e => console.error(e))
          }
          this.$emit('outTask', data.item2)
        })
        .catch(e => {
          this.$refs.toaster.pop('error', e.item1)
        })
        .finally(() => {
          this.closeParent()
          this.addManualTaskDialog = false
        })
    },
    valid: function() {
      if (!this.task.name) {
        return false
      }
      if (this.task.taskListType == null) {
        return false
      }

      if (this.task.step == null) {
        return false
      }
      if (!this.task.itemStatus || this.task.itemStatus.length <= 0) {
        return false
      }

      if (this.task.itemStatus && this.task.itemStatus.find(it => !it.name || (!it.displayOrder && it.displayOrder !== 0))) {
        return false
      }

      if (
        this.task.itemStatus.filter(i => {
          return i.required
        }).length <= 0
      ) {
        return false
      }

      return true
    }
  },
  mounted() {
    this.task = {}
    this.createNewTask()
  }
}
</script>

<style scoped></style>
